/**
 * A tenant's platform subscription status vocabulary.
 *
 * A LEAF subpath (`@puffin-core/status-taxonomy/subscription-status`) that requires nothing. A browser bundle that
 * needs only this vocabulary imports it here: the package ROOT's generated ESM half re-exports the
 * whole CommonJS surface, so importing the root carries every other status table into the bundle
 * (the console's vendor-core chunk went over budget that way; TOOLING-AND-DOCS-GEMS, 2026-09-30).
 * `constants.js` re-exports these, so there is ONE definition and the root surface is unchanged.
 *
 * @module @puffin-core/status-taxonomy/subscription-status
 */

'use strict';

/**
 * A tenant's platform subscription status (`public.companies."subscriptionStatus"`, CHECK-enforced to exactly these
 * values). Moved here from `@puffin-core/middleware` by USER-FACING-GEMS L5 (2026-09-29): the Platform Console shows it
 * as the tenant list's Billing column, and a web app cannot import middleware — the INVITATION_STATUS precedent.
 * Written at signup/activation and, since L5, by the Stripe subscription webhook sync (owner ruling Q-UFG-4).
 */
const SUBSCRIPTION_STATUS = Object.freeze({
  TRIALING: 'trialing',
  ACTIVE: 'active',
  PAST_DUE: 'past_due',
  CANCELED: 'canceled',
  EXPIRED: 'expired',
});

const SUBSCRIPTION_STATUS_VALUES = Object.freeze(Object.values(SUBSCRIPTION_STATUS));

/**
 * The subscription statuses that need platform staff's attention: money is owed, or the tenant is no longer paying.
 * The console raises its billing warning on exactly these.
 */
const BILLING_ATTENTION_SUBSCRIPTION_STATUSES = Object.freeze([
  SUBSCRIPTION_STATUS.PAST_DUE,
  SUBSCRIPTION_STATUS.CANCELED,
  SUBSCRIPTION_STATUS.EXPIRED,
]);

/**
 * Subscription status → badge TONE (`<StatusBadgeCell statusTones={…}>`, Q-MI-25), one entry per status so a new
 * status cannot render untoned. `canceled` is `neutral` (it was the off-set `badge-secondary`; neutral is the
 * estate's cancelled / archived tone). The values are the closed tone set's own strings, spelled literally because
 * this leaf requires nothing (`badgeTones.js`' `BADGE_TONE_VALUES` is what the tests judge them against).
 */
const SUBSCRIPTION_STATUS_BADGE_TONES = Object.freeze({
  [SUBSCRIPTION_STATUS.TRIALING]: 'info',
  [SUBSCRIPTION_STATUS.ACTIVE]: 'success',
  [SUBSCRIPTION_STATUS.PAST_DUE]: 'warning',
  [SUBSCRIPTION_STATUS.CANCELED]: 'neutral',
  [SUBSCRIPTION_STATUS.EXPIRED]: 'danger',
});

/**
 * How a tenant's plan is paid (`subscriptions."billingMode"`, CHECK-enforced to exactly these values; TENANT-PUBLIC-FK-001,
 * owner ruling 2026-10-05: "Complimentary Enterprise"). `stripe` — billed through Stripe, every sweep and reconcile
 * applies. `complimentary` — granted by the platform: no card, no charge, no trial end; every billing path decides on
 * this value EXPLICITLY (never inferred from absent Stripe ids) and leaves the plan alone.
 */
const SUBSCRIPTION_BILLING_MODE = Object.freeze({
  STRIPE: 'stripe',
  COMPLIMENTARY: 'complimentary',
});

const SUBSCRIPTION_BILLING_MODE_VALUES = Object.freeze(Object.values(SUBSCRIPTION_BILLING_MODE));

/**
 * The length of a new tenant's trial on a `stripe` plan, in days — the self-serve signup's and a staff-created
 * company's alike (owner ruling Q-FK-15: "Trial until card" — a staff-created company on a paid plan starts as a
 * trial of the standard length). A `complimentary` plan has no trial.
 */
const TRIAL_PERIOD_DAYS = 14;

module.exports = {
  SUBSCRIPTION_STATUS,
  SUBSCRIPTION_STATUS_VALUES,
  BILLING_ATTENTION_SUBSCRIPTION_STATUSES,
  SUBSCRIPTION_STATUS_BADGE_TONES,
  SUBSCRIPTION_BILLING_MODE,
  SUBSCRIPTION_BILLING_MODE_VALUES,
  TRIAL_PERIOD_DAYS,
};
